<template>
  <mia-form @submit="save">
    <mia-form-section title="Einkauf">
      <template #description>
        <mia-required-field-notice />
      </template>

      <mia-form-section-grid>
        <mia-has-permission permission="cars:purchasing:edit-purchasing-price-used" :when="() => car.item_type === 'Gebrauchtwagen'">
          <!-- Purchasing Price -->
          <div>
            <mia-car-edit-feature-input
              v-model="form.pricePurchased"
              :label="isDifferentialTax ? 'Einkaufspreis' : 'Netto Einkaufspreis'"
              input-class="text-right"
              :errors="getErrors('price_purchased')"
              addon="€"
              data-highlight="einkauf-netto-einkaufspreis"
              @input="onPricePurchasedInput"
            />
            <mia-has-feature feature="car_purchasing_price_calculator">
              <mia-purchasing-price-calculator
                class="mt-1"
                :car="car"
                @apply="onPurchasingPriceCalculatorApply"
              />
            </mia-has-feature>
          </div>

          <!-- Warranty Price -->
          <div>
            <mia-car-edit-feature-input
              v-model="form.purchasingWarrantyValue"
              label="Garantie zum Einkaufspreis"
              input-class="text-right"
              :errors="getErrors('purchasing_warranty_value')"
              addon="€"
              data-highlight="einkauf-netto-einkaufspreis"
              @input="clearErrors('purchasing_warranty_value')"
            />
          </div>
        </mia-has-permission>

        <!-- Purchase Invoice Number -->
        <div>
          <mia-car-edit-feature-input
            v-model="form.purchaseInvoiceNumber"
            label="EK-Rechnungsnummer"
            :errors="getErrors('purchase_invoice_number')"
            data-highlight="einkauf-ek-rechnungsnummer"
            @input="clearErrors('purchase_invoice_number')"
          />
        </div>

        <!-- Purchase Invoice Date -->
        <div>
          <mia-car-edit-feature-input
            v-model="form.purchaseInvoiceDate"
            label="EK-Rechnungsdatum"
            type="date"
            :errors="getErrors('purchase_invoice_date')"
            data-highlight="einkauf-ek-rechnungsdatum"
            @input="clearErrors('purchase_invoice_date')"
          />
        </div>

        <!-- Purchase Invoice Deadline -->
        <div>
          <mia-car-edit-feature-input
            v-model="form.purchaseInvoiceDeadline"
            label="EK-Zahlungsziel"
            type="date"
            :errors="getErrors('purchase_invoice_deadline')"
            data-highlight="einkauf-ek-zahlungsziel"
            @input="clearErrors('purchase_invoice_deadline')"
          />
        </div>
      </mia-form-section-grid>
    </mia-form-section>

    <mia-owns-car>
      <mia-is-allowed to="show_car_provisions">
        <div
          v-if="showProvisions"
          class="p-4"
        >
          <mia-car-provision-accountings-form
            ref="accountings"
            class="mt-8"
            data-highlight="bereits-gebuchte-praemienforderungen-und-erhaltene-zahlungen"
          />
        </div>
      </mia-is-allowed>
    </mia-owns-car>

    <template #actions>
      <div class="flex justify-between w-full">
        <mia-car-edit-section-progress
          page="prices"
          section="purchasingPriceData"
        />

        <div class="flex space-x-3">
          <mia-owns-car>
            <mia-car-edit-submit-button
              type="button"
              :disabled="$wait.any"
              :label="`Prämienzahlungen ${showProvisions ? 'ausblenden' : 'einblenden'}`"
              @click="showProvisions = !showProvisions"
            />
          </mia-owns-car>

          <mia-owns-car>
            <mia-car-edit-submit-button
              v-if="car.item_closed === 'N'"
              :disabled="$wait.any"
              :is-loading="$wait.is('updating purchasing price data')"
            />
            <mia-closed-hint v-else />
          </mia-owns-car>
        </div>
      </div>
    </template>
  </mia-form>
</template>

<script>
import numeral from 'numeral'
import 'numeral/locales/de'
import { getCar } from '@/api/car'
import { updatePurchasingPriceData } from '@/api/cars'
import Form from '@/components/ui/widgets/Form.vue'
import FormSection from '@/components/ui/widgets/FormSection.vue'
import FormSectionGrid from '@/components/ui/widgets/FormSectionGrid.vue'
import HandlesBackendValidationErrors from '@/mixins/HandlesBackendValidationErrors'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import SectionProgress from '@/components/cars/edit/SectionProgress.vue'
import { handleEmptyDate } from '@/helpers/utils'
import SubmitButton from '@/components/cars/edit/SubmitButton.vue'
import ClosedHint from '@/components/cars/ClosedHint.vue'
import RequiredFieldNotice from '@/components/cars/edit/forms/RequiredFieldNotice.vue'
import ProvisionAccountingsForm from '@/components/cars/edit/tables/ProvisionAccountingsForm.vue'
import PurchasingPriceCalculator from '@/components/cars/edit/PurchasingPriceCalculator.vue'

numeral.locale('de')

export default {
  name: 'MiaCarEditPurchasingPriceForm',

  components: {
    'mia-car-edit-feature-input': FeatureInput,
    'mia-car-edit-section-progress': SectionProgress,
    'mia-car-edit-submit-button': SubmitButton,
    'mia-car-provision-accountings-form': ProvisionAccountingsForm,
    'mia-closed-hint': ClosedHint,
    'mia-form-section-grid': FormSectionGrid,
    'mia-form-section': FormSection,
    'mia-form': Form,
    'mia-required-field-notice': RequiredFieldNotice,
    'mia-purchasing-price-calculator': PurchasingPriceCalculator
  },

  mixins: [HandlesBackendValidationErrors],

  data() {
    return {
      form: {
        pricePurchased: '',
        pricePurchasedSource: 'manual',
        purchasingWarrantyValue: '',
        purchaseInvoiceNumber: '',
        purchaseInvoiceDate: ''
      },
      showProvisions: false
    }
  },

  computed: {
    car: {
      get() {
        return this.$store.state.car.car
      },

      set(car) {
        this.$store.commit('setCar', car)
      }
    },

    isDifferentialTax() {
      return this.car.item_tax
    }
  },

  watch: {
    car: {
      immediate: true,
      deep: true,
      handler(car) {
        this.form.pricePurchased = `${car.item_price_purchased}`.replace('.', ',')
        this.form.purchasingWarrantyValue = `${car.item_purchasing_warranty_value || ''}`.replace('.', ',')
        this.form.purchaseInvoiceNumber = car.item_invoicenumber_purchased
        this.form.purchaseInvoiceDate = handleEmptyDate(car.item_invoicedate_purchased)
        this.form.purchaseInvoiceDeadline = handleEmptyDate(car.item_invoicedealine_purchased)
      }
    }
  },

  methods: {
    async save() {
      this.$wait.start('updating purchasing price data')

      try {
        await updatePurchasingPriceData(this.car.id, {
          price_purchased: numeral(this.form.pricePurchased).value(),
          price_purchased_source: this.form.pricePurchasedSource || 'manual',
          purchasing_warranty_value: numeral(this.form.purchasingWarrantyValue).value(),
          purchase_invoice_number: this.form.purchaseInvoiceNumber,
          purchase_invoice_date: this.form.purchaseInvoiceDate,
          purchase_invoice_deadline: this.form.purchaseInvoiceDeadline
        })

        this.car = await getCar({
          carId: this.car.id
        })

        window.eventBus.$emit('mia-info', {
          info: ['Daten erfolgreich gespeichert.']
        })
      } catch (e) {
        this.handleBackendValidationErrors(e)

        window.eventBus.$emit('mia-error', {
          error: ['Fehler beim Speichern der Daten.']
        })
      } finally {
        this.$wait.end('updating purchasing price data')
      }
    },

    onPurchasingPriceCalculatorApply(event) {
      this.$set(this.form, 'pricePurchased', `${event.value}`.replace('.', ','))
      this.$set(this.form, 'pricePurchasedSource', event.source || 'manual')
    },

    onPricePurchasedInput() {
      this.clearErrors('price_purchased')

      this.$set(this.form, 'pricePurchasedSource', 'manual')
    }
  }
}
</script>
